import { useMutation } from '@apollo/client';
import { message, Modal } from 'antd';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
    END_SORTING_BATCHES, EndSortingBatchesResponse, EndSortingBatchesVariables,
} from '../graphql/sortingBatch';
import { endSortingTargets } from '../utils/batchRules';
import { EndSortingModalProps } from './overlayProps';

/**
 * 结束分拣确认弹窗（原型 confirmCutoff / cutoffScope / cutoffPending / doCutoff）：
 * 列出所选批次与预计生效数量，仅「已创建 / 分拣中」批次会变为「分拣结束」；
 * 实际生效结果以后端返回为准（并发下批次状态可能已变化，预计数量仅供确认前参考）。
 */
export const EndSortingModal: React.FC<EndSortingModalProps> = ({ open, batches, onClose, onEnded }) => {
    const { t } = useTranslation();
    const [endSortingBatches, { loading }] = useMutation<EndSortingBatchesResponse, EndSortingBatchesVariables>(END_SORTING_BATCHES);

    const targets = useMemo(() => endSortingTargets(batches), [batches]);

    const handleConfirm = async () => {
        try {
            const { data } = await endSortingBatches({
                variables: { input: { batchIds: batches.map((b) => String(b.id)) } },
            });
            const result = data?.endSortingBatches;
            if (!result) return;
            if (result.batches.length) message.success(t('wms.sortingBatch.endSorting.ended', { count: result.batches.length }));
            else message.info(t('wms.sortingBatch.endSorting.allEnded'));
            onEnded();
            onClose();
        } catch (error: any) {
            const messageKey = error?.graphQLErrors?.[0]?.extensions?.messageKey;
            message.error(messageKey ? (t(messageKey) as string) || messageKey : t('wms.sortingBatch.endSorting.failed'));
        }
    };

    return (
        <Modal
            open={open}
            width={480}
            destroyOnHidden
            title={t('wms.sortingBatch.endSorting.title')}
            okText={t('wms.sortingBatch.endSorting.confirm')}
            cancelText={t('common.cancel')}
            confirmLoading={loading}
            onOk={handleConfirm}
            onCancel={onClose}
        >
            <div className="flex flex-col gap-3.5 py-1">
                <div className="text-[13px] leading-[1.8] text-[#1d2129]">{t('wms.sortingBatch.endSorting.confirmText')}</div>
                <div className="flex items-start gap-3">
                    <span className="w-[68px] flex-none pt-2 text-[13px] text-[#4e5969]">{t('wms.sortingBatch.endSorting.selectedBatches')}</span>
                    <div
                        data-testid="end-sorting-selected"
                        className="min-w-0 flex-1 break-all rounded-sm border border-solid border-[#f2f3f5] bg-[#f7f8fa] px-[11px] py-2 text-[13px] leading-[1.9] text-[#1d2129]"
                    >
                        {batches.length
                            ? batches.map((b) => <div key={b.id}>{b.batchNumber}</div>)
                            : t('wms.sortingBatch.common.none')}
                    </div>
                </div>
                <div className="flex items-center gap-3">
                    <span className="w-[68px] flex-none text-[13px] text-[#4e5969]">{t('wms.sortingBatch.endSorting.scope')}</span>
                    <span className="text-[13px] font-medium text-[#c26b00]">
                        {t('wms.sortingBatch.endSorting.effectCount', { count: targets.length })}
                    </span>
                </div>
                <div className="rounded border border-solid border-[#f2f3f5] bg-[#f7f8fa] px-[13px] py-[11px] text-xs leading-[1.8] text-[#4e5969]">
                    {t('wms.sortingBatch.endSorting.note')}
                </div>
            </div>
        </Modal>
    );
};
